/**
 * VLAN Manager
 *
 * Admin tool for managing VLAN 90 (Test VLAN) configuration
 * to test RMA gateways with various static IP configurations.
 *
 * LOCKED TO VLAN 90 ONLY for safety.
 */

import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from './axiosInstance';
import './styles/VlanManager.css';

const VlanManager = () => {
  // Current VLAN config from UDM
  const [currentConfig, setCurrentConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);

  // Gateway presets
  const [presets, setPresets] = useState([]);
  const [loadingPresets, setLoadingPresets] = useState(false);

  // New preset form
  const [showNewPreset, setShowNewPreset] = useState(false);
  const [newPreset, setNewPreset] = useState({
    name: '',
    description: '',
    gatewayIp: '',
    subnetMask: '24',
    dhcpStart: '',
    dhcpStop: ''
  });

  // Manual override form
  const [showManualOverride, setShowManualOverride] = useState(false);
  const [manualConfig, setManualConfig] = useState({
    gatewayIp: '',
    subnetMask: '24'
  });

  // Applying state
  const [applying, setApplying] = useState(false);
  const [confirmApply, setConfirmApply] = useState(null); // preset to confirm

  // VLAN routing state
  const [allNetworks, setAllNetworks] = useState([]);
  const [allowedVlans, setAllowedVlans] = useState([]);
  const [loadingNetworks, setLoadingNetworks] = useState(false);
  const [savingRouting, setSavingRouting] = useState(false);

  // Fetch current VLAN config
  const fetchCurrentConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosInstance.get('/vlan/current');
      setCurrentConfig(response.data);
    } catch (err) {
      console.error('Failed to fetch VLAN config:', err);
      setError(err.response?.data?.error || 'Failed to fetch current VLAN configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch gateway presets
  const fetchPresets = useCallback(async () => {
    try {
      setLoadingPresets(true);
      const response = await axiosInstance.get('/vlan/presets');
      setPresets(response.data);
    } catch (err) {
      console.error('Failed to fetch presets:', err);
    } finally {
      setLoadingPresets(false);
    }
  }, []);

  // Fetch all networks for VLAN routing
  const fetchNetworks = useCallback(async () => {
    try {
      setLoadingNetworks(true);
      const response = await axiosInstance.get('/vlan/networks');
      setAllNetworks(response.data.networks || []);
      setAllowedVlans(response.data.allowedNetworks || []);
    } catch (err) {
      console.error('Failed to fetch networks:', err);
    } finally {
      setLoadingNetworks(false);
    }
  }, []);

  useEffect(() => {
    fetchCurrentConfig();
    fetchPresets();
    fetchNetworks();
  }, [fetchCurrentConfig, fetchPresets, fetchNetworks]);

  // Apply a preset
  const applyPreset = async (preset) => {
    try {
      setApplying(true);
      setActionMessage(null);
      setError(null);

      const response = await axiosInstance.post('/vlan/apply', {
        gatewayIp: preset.gatewayIp,
        subnetMask: preset.subnetMask,
        dhcpStart: preset.dhcpStart,
        dhcpStop: preset.dhcpStop,
        presetName: preset.name
      });

      setActionMessage({
        type: 'success',
        text: `Applied "${preset.name}" configuration successfully!`
      });
      setConfirmApply(null);

      // Refresh current config
      await fetchCurrentConfig();
    } catch (err) {
      console.error('Failed to apply preset:', err);
      setError(err.response?.data?.error || 'Failed to apply configuration');
    } finally {
      setApplying(false);
    }
  };

  // Apply manual override
  const applyManualConfig = async () => {
    try {
      setApplying(true);
      setActionMessage(null);
      setError(null);

      const response = await axiosInstance.post('/vlan/apply', {
        gatewayIp: manualConfig.gatewayIp,
        subnetMask: manualConfig.subnetMask,
        presetName: 'Manual Override'
      });

      setActionMessage({
        type: 'success',
        text: 'Applied manual configuration successfully!'
      });
      setShowManualOverride(false);

      // Refresh current config
      await fetchCurrentConfig();
    } catch (err) {
      console.error('Failed to apply manual config:', err);
      setError(err.response?.data?.error || 'Failed to apply configuration');
    } finally {
      setApplying(false);
    }
  };

  // Save new preset
  const saveNewPreset = async () => {
    try {
      if (!newPreset.name || !newPreset.gatewayIp) {
        setError('Name and Gateway IP are required');
        return;
      }

      const response = await axiosInstance.post('/vlan/presets', newPreset);
      setActionMessage({
        type: 'success',
        text: `Preset "${newPreset.name}" saved!`
      });
      setShowNewPreset(false);
      setNewPreset({
        name: '',
        description: '',
        gatewayIp: '',
        subnetMask: '24',
        dhcpStart: '',
        dhcpStop: ''
      });

      // Refresh presets
      await fetchPresets();
    } catch (err) {
      console.error('Failed to save preset:', err);
      setError(err.response?.data?.error || 'Failed to save preset');
    }
  };

  // Delete preset
  const deletePreset = async (presetId) => {
    if (!window.confirm('Are you sure you want to delete this preset?')) return;

    try {
      await axiosInstance.delete(`/vlan/presets/${presetId}`);
      setActionMessage({
        type: 'success',
        text: 'Preset deleted'
      });
      await fetchPresets();
    } catch (err) {
      console.error('Failed to delete preset:', err);
      setError(err.response?.data?.error || 'Failed to delete preset');
    }
  };

  // Toggle VLAN in allowed list
  const toggleVlan = (vlanId) => {
    setAllowedVlans(prev => {
      if (prev.includes(vlanId)) {
        return prev.filter(id => id !== vlanId);
      } else {
        return [...prev, vlanId];
      }
    });
  };

  // Save VLAN routing
  const saveRouting = async () => {
    try {
      setSavingRouting(true);
      setError(null);
      await axiosInstance.post('/vlan/routing', { allowedNetworkIds: allowedVlans });
      setActionMessage({
        type: 'success',
        text: 'VLAN routing updated successfully!'
      });
    } catch (err) {
      console.error('Failed to save routing:', err);
      setError(err.response?.data?.error || 'Failed to save routing');
    } finally {
      setSavingRouting(false);
    }
  };

  // Parse subnet for display
  const parseSubnet = (ipSubnet) => {
    if (!ipSubnet) return { ip: '', mask: '' };
    const [ip, mask] = ipSubnet.split('/');
    return { ip, mask };
  };

  const currentSubnet = currentConfig ? parseSubnet(currentConfig.ip_subnet) : { ip: '', mask: '' };

  return (
    <div className="vlan-manager">
      <div className="vlan-header">
        <h1>VLAN Manager</h1>
        <p className="vlan-subtitle">
          Manage VLAN 90 (Test VLAN) for RMA gateway testing
        </p>
      </div>

      {/* Messages */}
      {error && (
        <div className="vlan-message error">
          <span className="icon">!</span>
          {error}
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}
      {actionMessage && (
        <div className={`vlan-message ${actionMessage.type}`}>
          <span className="icon">{actionMessage.type === 'success' ? '✓' : 'i'}</span>
          {actionMessage.text}
          <button onClick={() => setActionMessage(null)}>Dismiss</button>
        </div>
      )}

      {/* Current Configuration */}
      <div className="vlan-section">
        <div className="section-header">
          <h2>Current Configuration</h2>
          <button
            className="btn-refresh"
            onClick={fetchCurrentConfig}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>

        {loading ? (
          <div className="vlan-loading">Loading current configuration...</div>
        ) : currentConfig ? (
          <div className="current-config-grid">
            <div className="config-item">
              <label>VLAN Name</label>
              <span className="config-value">{currentConfig.name}</span>
            </div>
            <div className="config-item">
              <label>VLAN ID</label>
              <span className="config-value vlan-id">{currentConfig.vlan}</span>
            </div>
            <div className="config-item">
              <label>Gateway IP</label>
              <span className="config-value highlight">{currentSubnet.ip}</span>
            </div>
            <div className="config-item">
              <label>Subnet Mask</label>
              <span className="config-value">/{currentSubnet.mask}</span>
            </div>
            <div className="config-item">
              <label>DHCP Range</label>
              <span className="config-value">
                {currentConfig.dhcpd_start} - {currentConfig.dhcpd_stop}
              </span>
            </div>
            <div className="config-item">
              <label>DHCP Enabled</label>
              <span className={`config-value badge ${currentConfig.dhcpd_enabled ? 'green' : 'gray'}`}>
                {currentConfig.dhcpd_enabled ? 'Yes' : 'No'}
              </span>
            </div>
          </div>
        ) : (
          <div className="vlan-error">Unable to load configuration</div>
        )}
      </div>

      {/* Gateway Presets */}
      <div className="vlan-section">
        <div className="section-header">
          <h2>Gateway Presets</h2>
          <div className="header-actions">
            <button
              className="btn-secondary"
              onClick={() => setShowManualOverride(!showManualOverride)}
            >
              Manual Override
            </button>
            <button
              className="btn-primary"
              onClick={() => setShowNewPreset(!showNewPreset)}
            >
              + Add Preset
            </button>
          </div>
        </div>

        {/* Manual Override Form */}
        {showManualOverride && (
          <div className="preset-form manual-form">
            <h3>Manual Override</h3>
            <p className="form-description">
              Quickly apply a custom configuration without saving a preset
            </p>
            <div className="form-grid">
              <div className="form-group">
                <label>Gateway IP *</label>
                <input
                  type="text"
                  placeholder="e.g., 10.112.143.99"
                  value={manualConfig.gatewayIp}
                  onChange={(e) => setManualConfig({ ...manualConfig, gatewayIp: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Subnet Mask</label>
                <select
                  value={manualConfig.subnetMask}
                  onChange={(e) => setManualConfig({ ...manualConfig, subnetMask: e.target.value })}
                >
                  <option value="24">/24 (255.255.255.0)</option>
                  <option value="25">/25 (255.255.255.128)</option>
                  <option value="26">/26 (255.255.255.192)</option>
                  <option value="27">/27 (255.255.255.224)</option>
                  <option value="23">/23 (255.255.254.0)</option>
                  <option value="22">/22 (255.255.252.0)</option>
                </select>
              </div>
            </div>
            <div className="form-actions">
              <button className="btn-cancel" onClick={() => setShowManualOverride(false)}>
                Cancel
              </button>
              <button
                className="btn-apply"
                onClick={applyManualConfig}
                disabled={applying || !manualConfig.gatewayIp}
              >
                {applying ? 'Applying...' : 'Apply Now'}
              </button>
            </div>
          </div>
        )}

        {/* New Preset Form */}
        {showNewPreset && (
          <div className="preset-form">
            <h3>Add New Gateway Preset</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Preset Name *</label>
                <input
                  type="text"
                  placeholder="e.g., Walmart RMA Gateway #1"
                  value={newPreset.name}
                  onChange={(e) => setNewPreset({ ...newPreset, name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <input
                  type="text"
                  placeholder="e.g., Serial: ABC123, Customer: Walmart"
                  value={newPreset.description}
                  onChange={(e) => setNewPreset({ ...newPreset, description: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Gateway IP *</label>
                <input
                  type="text"
                  placeholder="e.g., 10.112.143.99"
                  value={newPreset.gatewayIp}
                  onChange={(e) => setNewPreset({ ...newPreset, gatewayIp: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Subnet Mask</label>
                <select
                  value={newPreset.subnetMask}
                  onChange={(e) => setNewPreset({ ...newPreset, subnetMask: e.target.value })}
                >
                  <option value="24">/24 (255.255.255.0)</option>
                  <option value="25">/25 (255.255.255.128)</option>
                  <option value="26">/26 (255.255.255.192)</option>
                  <option value="27">/27 (255.255.255.224)</option>
                  <option value="23">/23 (255.255.254.0)</option>
                  <option value="22">/22 (255.255.252.0)</option>
                </select>
              </div>
              <div className="form-group">
                <label>DHCP Start (optional)</label>
                <input
                  type="text"
                  placeholder="e.g., 10.112.143.2"
                  value={newPreset.dhcpStart}
                  onChange={(e) => setNewPreset({ ...newPreset, dhcpStart: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>DHCP Stop (optional)</label>
                <input
                  type="text"
                  placeholder="e.g., 10.112.143.126"
                  value={newPreset.dhcpStop}
                  onChange={(e) => setNewPreset({ ...newPreset, dhcpStop: e.target.value })}
                />
              </div>
            </div>
            <div className="form-actions">
              <button className="btn-cancel" onClick={() => setShowNewPreset(false)}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={saveNewPreset}
                disabled={!newPreset.name || !newPreset.gatewayIp}
              >
                Save Preset
              </button>
            </div>
          </div>
        )}

        {/* Preset List */}
        {loadingPresets ? (
          <div className="vlan-loading">Loading presets...</div>
        ) : presets.length === 0 ? (
          <div className="no-presets">
            <p>No gateway presets saved yet.</p>
            <p>Click "+ Add Preset" to create your first one.</p>
          </div>
        ) : (
          <div className="presets-grid">
            {presets.map((preset) => (
              <div
                key={preset._id}
                className={`preset-card ${confirmApply?._id === preset._id ? 'confirming' : ''}`}
              >
                <div className="preset-header">
                  <h3>{preset.name}</h3>
                  <button
                    className="btn-delete"
                    onClick={() => deletePreset(preset._id)}
                    title="Delete preset"
                  >
                    x
                  </button>
                </div>
                {preset.description && (
                  <p className="preset-description">{preset.description}</p>
                )}
                <div className="preset-details">
                  <div className="detail">
                    <span className="label">Gateway:</span>
                    <span className="value">{preset.gatewayIp}</span>
                  </div>
                  <div className="detail">
                    <span className="label">Subnet:</span>
                    <span className="value">/{preset.subnetMask}</span>
                  </div>
                  {preset.dhcpStart && preset.dhcpStop && (
                    <div className="detail">
                      <span className="label">DHCP:</span>
                      <span className="value">{preset.dhcpStart} - {preset.dhcpStop}</span>
                    </div>
                  )}
                </div>

                {confirmApply?._id === preset._id ? (
                  <div className="confirm-actions">
                    <p className="confirm-text">Apply this configuration?</p>
                    <button
                      className="btn-cancel"
                      onClick={() => setConfirmApply(null)}
                      disabled={applying}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn-confirm"
                      onClick={() => applyPreset(preset)}
                      disabled={applying}
                    >
                      {applying ? 'Applying...' : 'Confirm'}
                    </button>
                  </div>
                ) : (
                  <button
                    className="btn-apply-preset"
                    onClick={() => setConfirmApply(preset)}
                  >
                    Apply Configuration
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* VLAN Routing */}
      <div className="vlan-section">
        <div className="section-header">
          <h2>VLAN 90 Can Talk To:</h2>
          <button
            className="btn-refresh"
            onClick={fetchNetworks}
            disabled={loadingNetworks}
          >
            {loadingNetworks ? 'Loading...' : 'Refresh'}
          </button>
        </div>

        {loadingNetworks ? (
          <div className="vlan-loading">Loading networks...</div>
        ) : allNetworks.length === 0 ? (
          <div className="vlan-error">No networks found</div>
        ) : (
          <>
            <div className="vlan-routing-grid">
              {allNetworks
                .filter(net => net.vlan !== 90) // Don't show VLAN 90 itself
                .map(net => (
                  <label
                    key={net.id}
                    className={`vlan-checkbox-item ${allowedVlans.includes(net.id) ? 'checked' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={allowedVlans.includes(net.id)}
                      onChange={() => toggleVlan(net.id)}
                    />
                    <div className="vlan-info">
                      <div className="vlan-name">{net.name}</div>
                      <div className="vlan-id-tag">VLAN {net.vlan} • {net.subnet}</div>
                    </div>
                  </label>
                ))}
            </div>
            <div className="routing-actions">
              <button
                className="btn-apply"
                onClick={saveRouting}
                disabled={savingRouting}
              >
                {savingRouting ? 'Saving...' : 'Save Routing'}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Safety Notice */}
      <div className="vlan-section safety-notice">
        <h3>Safety Information</h3>
        <ul>
          <li>This tool only modifies VLAN 90 (Test VLAN)</li>
          <li>Changes take effect immediately</li>
          <li>The main network VLANs are not affected</li>
          <li>DHCP changes may require devices to renew their leases</li>
        </ul>
      </div>
    </div>
  );
};

export default VlanManager;
